import { Play, ArrowRight, Boxes, Cog, BarChart3, FileText, Shield, Target, Sparkles } from 'lucide-react';
import { Link } from 'react-router';

const iconMap: { [key: string]: any } = {
  Sparkles, Boxes, Cog, BarChart3, FileText, Shield, Target,
};

interface ServiceItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  link: string;
  videoUrl: string;
  videoId: string;
  gradient: string;
  overlayEnabled: boolean;
}

interface ServicesGridProps {
  items: ServiceItem[];
  onVideoClick: (videoUrl: string, title: string) => void;
}

// ============================================
// SKELETON — exported so HomePage can use it
// while servicesLoading is true
// ============================================

export function ServicesGridSkeleton() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/60 shadow-lg"
        >
          {/* Thumbnail placeholder */}
          <div className="aspect-video bg-gray-200 relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>

          {/* Content placeholder */}
          <div className="p-6 space-y-4">
            {/* Icon */}
            <div className="w-12 h-12 bg-gray-200 rounded-xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Title */}
            <div className="h-5 bg-gray-200 rounded w-3/4 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Description */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-4 bg-gray-200 rounded w-5/6 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
            {/* CTA link */}
            <div className="h-4 bg-gray-200 rounded w-24 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================
// COMPONENT
// ============================================

export function ServicesGrid({ items, onVideoClick }: ServicesGridProps) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((service) => {
        const ServiceIcon = iconMap[service.icon] || Boxes;
        
        return (
          <div key={service.id} className="group relative bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/60 hover:border-white/80 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
            {/* Gradient Hover Glow */}
            <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
            
            {/* Video Thumbnail */}
            <div 
              className="relative aspect-video cursor-pointer overflow-hidden bg-black"
              onClick={() => onVideoClick(service.videoUrl, service.title)}
            >
              <img 
                src={`https://img.youtube.com/vi/${service.videoId}/maxresdefault.jpg`}
                alt={service.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.src = `https://img.youtube.com/vi/${service.videoId}/hqdefault.jpg`;
                }}
              />
              
              {service.overlayEnabled && (
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-40 group-hover:opacity-0 transition-opacity duration-300 pointer-events-none`} />
              )}
              
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#D74444] via-[#E87D3E] to-[#E8B84D] rounded-full blur-xl opacity-60" />
                  <div className="relative bg-white/95 backdrop-blur-md rounded-full w-16 h-16 flex items-center justify-center shadow-2xl">
                    <Play className="w-8 h-8 text-[#2F2E5C] ml-1" fill="currentColor" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="relative p-6">
              <div className={`relative w-12 h-12 bg-gradient-to-br ${service.gradient} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} blur-md opacity-50 group-hover:opacity-75 transition-opacity`} />
                <div className="relative z-10">
                  <ServiceIcon className="w-6 h-6" />
                </div>
              </div>
              
              <h3 className="font-bold mb-2 text-[#2F2E5C] group-hover:bg-gradient-to-r group-hover:from-[#2F2E5C] group-hover:to-[#4a487a] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                {service.title}
              </h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">{service.description}</p>
              
              <Link 
                to={service.link}
                onClick={(e) => e.stopPropagation()}
                className={`inline-flex items-center font-bold gap-2 text-sm group-hover:gap-3 transition-all duration-300 bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}
              >
                Learn More
                <ArrowRight className="w-4 h-4 text-[#D74444] group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}